import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, ResponsiveContainer, XAxis, YAxis, Tooltip } from "recharts";
import { api, money } from "../../lib/api";

const COLORS = ["#FACC15", "#10B981", "#3B82F6", "#F97316", "#A855F7", "#EC4899", "#22D3EE", "#F43F5E"];

function Panel({ title, children }) {
  return (
    <div className="bg-[#121216] border border-white/10 rounded-lg p-5">
      <h3 className="font-heading font-bold uppercase text-white mb-4">{title}</h3>
      {children}
    </div>
  );
}

export default function AdminAnalytics() {
  const [params] = useSearchParams();
  const [b, setB] = useState(null);
  const [academies, setAcademies] = useState([]);
  const [academyId, setAcademyId] = useState(params.get("academy") || "");
  const [aStats, setAStats] = useState(null);

  useEffect(() => {
    api.get("/admin/analytics/breakdown").then(({ data }) => setB(data)).catch(() => {});
    api.get("/academies").then(({ data }) => setAcademies(data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (academyId) api.get(`/admin/analytics/academy/${academyId}`).then(({ data }) => setAStats(data)).catch(() => setAStats(null));
    else setAStats(null);
  }, [academyId]);

  return (
    <div className="px-5 lg:px-8 py-8">
      <p className="font-mono text-yellow-400 uppercase tracking-widest text-xs">Analytics</p>
      <h1 className="font-heading font-black uppercase text-4xl text-white mt-1 mb-6">Business Intelligence</h1>

      {b && (
        <div className="grid lg:grid-cols-2 gap-4">
          <Panel title="Revenue by Academy">
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={b.by_academy.slice(0, 8)} layout="vertical" margin={{ left: 20 }}>
                <XAxis type="number" stroke="#666" fontSize={11} />
                <YAxis type="category" dataKey="name" stroke="#666" fontSize={10} width={120} />
                <Tooltip contentStyle={{ background: "#0A0A0C", border: "1px solid #333" }} cursor={{ fill: "#ffffff08" }} formatter={(v) => money(v)} />
                <Bar dataKey="revenue" fill="#10B981" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </Panel>
          <Panel title="Top Products by Units">
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={b.by_product.slice(0, 8)} layout="vertical" margin={{ left: 20 }}>
                <XAxis type="number" stroke="#666" fontSize={11} />
                <YAxis type="category" dataKey="name" stroke="#666" fontSize={10} width={120} />
                <Tooltip contentStyle={{ background: "#0A0A0C", border: "1px solid #333" }} cursor={{ fill: "#ffffff08" }} />
                <Bar dataKey="units" fill="#FACC15" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </Panel>
          <Panel title="Units by Size">
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={b.by_size}>
                <XAxis dataKey="size" stroke="#666" fontSize={11} />
                <YAxis stroke="#666" fontSize={11} />
                <Tooltip contentStyle={{ background: "#0A0A0C", border: "1px solid #333" }} cursor={{ fill: "#ffffff08" }} />
                <Bar dataKey="units" fill="#3B82F6" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </Panel>
          <Panel title="Adult vs Youth (Units)">
            <ResponsiveContainer width="100%" height={240}>
              <PieChart>
                <Pie data={Object.entries(b.by_audience).map(([k, v]) => ({ name: k, value: v }))} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={90} label>
                  {Object.keys(b.by_audience).map((k, i) => <Cell key={i} fill={COLORS[i]} />)}
                </Pie>
                <Tooltip contentStyle={{ background: "#0A0A0C", border: "1px solid #333" }} />
              </PieChart>
            </ResponsiveContainer>
          </Panel>
          <Panel title="Revenue Over Time">
            <ResponsiveContainer width="100%" height={240}>
              <LineChart data={b.time_series}>
                <XAxis dataKey="date" stroke="#666" fontSize={10} />
                <YAxis stroke="#666" fontSize={11} />
                <Tooltip contentStyle={{ background: "#0A0A0C", border: "1px solid #333" }} formatter={(v) => money(v)} />
                <Line type="monotone" dataKey="revenue" stroke="#FACC15" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </Panel>
          <Panel title="Revenue by Category">
            <ResponsiveContainer width="100%" height={240}>
              <PieChart>
                <Pie data={b.by_category} dataKey="revenue" nameKey="name" cx="50%" cy="50%" outerRadius={90}>
                  {b.by_category.map((e, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip contentStyle={{ background: "#0A0A0C", border: "1px solid #333" }} formatter={(v) => money(v)} />
              </PieChart>
            </ResponsiveContainer>
          </Panel>
        </div>
      )}

      <div className="mt-8">
        <div className="flex items-center gap-3 mb-4">
          <h2 className="font-heading font-bold uppercase text-2xl text-white">Academy Deep Dive</h2>
          <select data-testid="analytics-academy" value={academyId} onChange={(e) => setAcademyId(e.target.value)} className="bg-[#121216] border border-white/15 rounded px-3 py-2 text-sm text-white">
            <option value="">Select academy…</option>
            {academies.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
          </select>
        </div>
        {aStats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              ["Retail Sales", money(aStats.retail_sales)],
              ["Wholesale Sales", money(aStats.wholesale_sales)],
              ["Total Units", aStats.total_units],
              ["Customers", aStats.customers],
              ["Retail Orders", aStats.retail_orders],
              ["Wholesale Orders", aStats.wholesale_orders],
              ["Avg Retail Order", money(aStats.avg_retail_order)],
              ["Avg Wholesale Order", money(aStats.avg_wholesale_order)],
            ].map(([l, v]) => (
              <div key={l} className="bg-[#121216] border border-white/10 rounded-lg p-4">
                <p className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">{l}</p>
                <p className="font-heading font-black text-2xl text-white mt-1">{v}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
